// Singleton wrapper for the netcountable-e2ee (NC1 key layer) API — same
// shape as SurveyApi/AccountApi. Register it in src/lib/auth.js
// _applyCredentials (E2eeApi.inited = false) so it picks up fresh SigV4
// credentials after every token refresh.
//
// NC1 v2 (EPOCH-KEYPAIR.md): the identityDelivery operations are retired;
// uploader enrollments carry only devSignPk and their grant relays no secret.

import e2eeClientFactory from './apigClient'
import AWSClockSkewHandler from '../AWSClockSkewHandler'
import store from "@/store"
import api from '@/lib/api'

// API Gateway's IAM authorizer answers a request signed with EXPIRED identity
// pool credentials with 403 {"message":"The security token included in the
// request is expired"} and never invokes the Lambda. The main API client
// refreshes credentials on its own calls and on tab focus; this client only
// learns about fresh ones through `inited = false`. A long key-layer
// sequence (member list, then envelopes, seconds apart) therefore used to
// die mid-way when the hour-old credentials expired between two calls —
// live on dev 2026-10-08, which stranded an epoch. Recognise that reply and
// retry once with refreshed credentials.
function isExpiredCredentialsError(err) {
  const status = err && err.response && err.response.status
  const data = err && err.response && err.response.data
  const message = data && typeof data.message === 'string' ? data.message : ''
  return status === 403 && /security token|expired|invalid.*token/i.test(message)
}

class E2eeApi {
  constructor() {
    this.inited = false
    this.client = null
  }

  // The env var is a placeholder until the netcountable-e2ee service is
  // deployed for the stage. Callers (the signup hook) must check this and
  // no-op — never break signup because the key layer isn't reachable.
  isConfigured() {
    return !!process.env.VUE_APP_E2EE_URL
  }

  init() {
    if(!this.inited) {
      if(!this.isConfigured()) {
        throw new Error('VUE_APP_E2EE_URL is not set for this stage')
      }
      this.inited = true
      this.client = e2eeClientFactory.newClient(store.state.credentials, process.env.VUE_APP_E2EE_URL)
    }
  }

  async _call(fn, { retried = false } = {}) {
    this.init()

    let error
    let result = await AWSClockSkewHandler.handleClockSkew(fn).catch(err => {
      error = err
    })
    if(error) {
      if(!retried && isExpiredCredentialsError(error)) {
        console.log('[nc1] AWS credentials expired mid-session - refreshing and retrying once')
        await api.refreshCredentialsIfNeeded() // re-applies credentials; sets this.inited = false
        return this._call(fn, { retried: true })
      }
      const data = error.response && error.response.data
      if(data && typeof data.error === 'string') {
        // keep the original (status, response) reachable, but lead with the service's own words
        const readable = new Error(data.error)
        readable.code = data.code           // e.g. 'ACCOUNT_LOCKED'
        readable.current = data.current     // 409 BUNDLE_CONFLICT: the bundle now stored (+ _rev)
        readable.response = error.response
        readable.cause = error
        throw readable
      }
      throw error
    }

    return result
  }

  // ---- account ----

  async getAccount(email) {
    return await this._call(() => this.client.e2eeAccountGet({ email }))
  }

  /** payload = { email, identityBundle, deviceList, memberList, adkEnvelopes, log } — one validated transaction. */
  async bootstrapAccount(payload) {
    return await this._call(() => this.client.e2eeAccountPost({ email: payload.email }, payload))
  }

  /**
   * Finding ⑯: viewer-only identity reset — same payload shape as the
   * bootstrap but at identityVersion old+1; the service re-genesises the
   * partition. 409 for accounts with devices or other members.
   * OWNER-RESET.md (2026-09-22): with `scope: 'owner'` + `idToken` (a sign-in
   * ≤ 5 min old) the same route resets an account WITH devices and members:
   * 401 REAUTH_REQUIRED / 403 ACCOUNT_LOCKED, else a phased wipe + new
   * genesis + queued history purge → { identityVersion, resetAt, purge }.
   */
  async resetAccountIdentity(payload) {
    return await this._call(() => this.client.e2eeAccountResetPost({ email: payload.email }, payload))
  }

  /**
   * The account-deletion key wipe (OWNER-RESET.md §15, 2026-10-08): the same
   * route with `scope: 'delete'` + `idToken` (a sign-in ≤ 5 min old) and no
   * genesis — the ENTIRE partition goes (identity + wraps, lists, epoch
   * secrets, chain, audit rows) and `e2ee` / `e2eeHead` leave the account
   * row; nothing replaces them. 401 REAUTH_REQUIRED / 403 ACCOUNT_LOCKED /
   * 409 while the account still has devices or recipients (DELETE /account's
   * own preconditions). 200 even with nothing to wipe →
   * { altAccountId, hadIdentity, removed, resumed }.
   */
  async deleteAccountIdentity(email, idToken) {
    return await this._call(() => this.client.e2eeAccountResetPost({ email }, { email, scope: 'delete', idToken }))
  }

  /** §8.5 invites: email -> PUBLIC identity keys ({userId, idEncPk, idSignPk}); 404 = no NC1 identity. */
  async resolveIdentity(email) {
    return await this._call(() => this.client.e2eeResolveGet({ email }))
  }

  // ---- key documents ----

  /**
   * The bundle as stored, with the row's `_rev` token split off into `rev`
   * (2026-10-08) so `data` is exactly the signed document — callers that
   * spread `data` into a rewrite never carry `_rev` into the signature.
   */
  async getIdentityBundle(email) {
    const res = await this._call(() => this.client.e2eeIdentityBundleGet({ email }))
    const { _rev, ...bundle } = res.data || {}
    return { ...res, data: bundle, rev: _rev || null }
  }

  /**
   * sig = detached identity signature over the canonical bundle (base64).
   * `baseRev` (2026-10-08): the `rev` the bundle was read with; the service
   * refuses the write with 409 BUNDLE_CONFLICT (error.current = the bundle
   * now stored) when another session changed it in between.
   */
  async putIdentityBundle(email, value, sig, baseRev = null) {
    const body = { email, value, sig, ...(baseRev ? { baseRev } : {}) }
    return await this._call(() => this.client.e2eeIdentityBundlePost({ email }, body))
  }

  async getDeviceList(email) {
    return await this._call(() => this.client.e2eeDeviceListGet({ email }))
  }

  /** Chain-committed write: logEntry lands transactionally with the doc. */
  async putDeviceList(email, value, logEntry) {
    return await this._call(() => this.client.e2eeDeviceListPost({ email }, { email, value, logEntry }))
  }

  async getMemberList(email) {
    return await this._call(() => this.client.e2eeMemberListGet({ email }))
  }

  /**
   * Chain-committed write; the member list carries the current epochPk.
   * `adkEnvelopes` + `adkSig` (2026-10-08): the COMPLETE envelope set, landed
   * in the same service transaction as the list — required for an epoch change.
   */
  async putMemberList(email, value, logEntry, adkEnvelopes = null, adkSig = null) {
    const body = { email, value, logEntry, ...(adkEnvelopes ? { adkEnvelopes, adkSig } : {}) }
    return await this._call(() => this.client.e2eeMemberListPost({ email }, body))
  }

  /** The epoch SECRETS sealed per member (one envelope per epoch per member; one document server-side since 2026-10-08). */
  async getAdkEnvelopes(email) {
    return await this._call(() => this.client.e2eeAdkEnvelopesGet({ email }))
  }

  /** Custody-signed full-set replacement (envelopes absent from `value` are withdrawn). Epoch changes ride putMemberList instead. */
  async putAdkEnvelopes(email, value, sig) {
    return await this._call(() => this.client.e2eeAdkEnvelopesPost({ email }, { email, value, sig }))
  }

  /**
   * Chunk 7: permanently purge records before keepFromMs (custody-signed).
   * 2026-09-14: answers 202 { jobId, state: 'queued' } — poll getPurgeStatus.
   */
  async purgeRecords(email, keepFromMs, sig) {
    return await this._call(() => this.client.e2eeRecordsPurgePost({ email }, { email, keepFromMs, sig }))
  }

  /** One purge job's row: { jobId, state: queued|running|done|failed, removedEvents?, removedImages?, error? }. */
  async getPurgeStatus(email, jobId) {
    return await this._call(() => this.client.e2eeRecordsPurgeGet({ email, jobId }))
  }

  // ---- enrollments (chunk 2; v2 shapes) ----

  /**
   * viewer:   { email, deviceUuid, deviceName, purpose: 'viewer', transferPk, devSignPk, devEncPk, fingerprint }
   * uploader: { email, deviceUuid, deviceName, purpose: 'uploader', devSignPk }
   */
  async createEnrollment(payload) {
    return await this._call(() => this.client.e2eeEnrollmentsPost({ email: payload.email }, payload))
  }

  /** Approver list: every live request for the account (no sealed bundles). */
  async listEnrollments(email) {
    return await this._call(() => this.client.e2eeEnrollmentsListGet({ email }))
  }

  /** Requester poll: state + sealedBundle once granted (viewer) or deviceList + memberList (uploader). */
  async pollEnrollment(email, deviceUuid) {
    return await this._call(() => this.client.e2eeEnrollmentsPollGet({ email, deviceUuid }))
  }

  /**
   * body = { email, sealedBundle, sig } (viewer; sig over {requestId, transferPk, sealedBundle}) or
   *        { email, deviceList, logEntry, sig } (uploader; sig over {requestId, devSignPk}).
   */
  async acceptEnrollment(email, requestId, body) {
    return await this._call(() => this.client.e2eeEnrollmentAcceptPost({ email, requestId }, body))
  }

  async rejectEnrollment(email, requestId) {
    return await this._call(() => this.client.e2eeEnrollmentRejectPost({ email, requestId }, { email }))
  }

  // ---- invite links (INVITE-LINK-PASSKEY-FIRST.md §1.3) ----

  /** Owner: register a recipient's wrapped invite key; `sig` = custody signature over canon(value). */
  async createInvite(ownerEmail, value, sig) {
    return await this._call(() => this.client.e2eeInvitesPost({ email: ownerEmail }, { email: ownerEmail, value, sig }))
  }

  /** Owner: the account's unexpired invites (pending + claimed), without the wrapped blobs. */
  async listInvites(ownerEmail) {
    return await this._call(() => this.client.e2eeInvitesListGet({ email: ownerEmail }))
  }

  /** Recipient: one invite row incl. the wrapped invite key — served only to a session signed in as the invited email. */
  async getInvite(ownerUserId, inviteId) {
    return await this._call(() => this.client.e2eeInviteGet({ owner: ownerUserId, inviteId }))
  }

  /** Recipient: flip the invite pending → claimed once the invite key is stored in their bundle. */
  async claimInvite(recipientEmail, ownerUserId, inviteId) {
    return await this._call(() => this.client.e2eeInviteClaimPost({ email: recipientEmail, inviteId }, { email: recipientEmail, owner: ownerUserId }))
  }

  /** Owner: drop an invite row (the member entry itself is withdrawn by Members.removeMember). */
  async revokeInvite(ownerEmail, inviteId) {
    return await this._call(() => this.client.e2eeInviteRevokePost({ email: ownerEmail, inviteId }, { email: ownerEmail }))
  }

  // ---- transparency log ----

  async getLog(email, from) {
    let params = { email }
    if(from !== undefined && from !== null) {
      params.from = from
    }
    return await this._call(() => this.client.e2eeLogGet(params))
  }

  async appendLog(email, entry) {
    return await this._call(() => this.client.e2eeLogPost({ email }, { email, value: entry }))
  }
}

export default (new E2eeApi())
